import { useState, useEffect, useCallback } from "react";
import { Plus, Edit2, CheckCircle2, XCircle, Globe, Building2, AlertCircle, X, Shield } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { isSuperAdmin } from "../../lib/permissions";

export default function Settings({ user }) {
  const isSuper = isSuperAdmin(user);

  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(true);

  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [companyName, setCompanyName] = useState("");
  const [companyDomain, setCompanyDomain] = useState("");
  const [modalBusy, setModalBusy] = useState(false);
  const [modalError, setModalError] = useState("");

  const loadCompanies = useCallback(async () => {
    setLoadingCompanies(true);
    try {
      const res = await api("get", "/companies");
      if (Array.isArray(res.data)) {
        setCompanies(res.data);
      }
    } catch (err) {
      console.error("Failed to fetch companies:", err);
      toast.error("Could not load companies");
    } finally {
      setLoadingCompanies(false);
    }
  }, []);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  const handleOpenAdd = () => {
    setEditingCompany(null);
    setCompanyName("");
    setCompanyDomain("");
    setModalError("");
    setModalOpen(true);
  };

  const handleOpenEdit = (company) => {
    setEditingCompany(company);
    setCompanyName(company.name || "");
    setCompanyDomain(company.domain || "");
    setModalError("");
    setModalOpen(true);
  };

  const handleToggleStatus = async (company) => {
    const nextStatus = !company.is_active;
    const actionLabel = nextStatus ? "activate" : "deactivate";
    try {
      await api("patch", `/companies/${company.company_id}/status`, { is_active: nextStatus });
      toast.success(`Company '${company.name}' ${nextStatus ? "activated" : "deactivated"}`);
      loadCompanies();
    } catch (err) {
      const msg = err.response?.data?.detail || `Failed to ${actionLabel} company`;
      toast.error(msg);
    }
  };

  const handleSaveCompany = async (e) => {
    e.preventDefault();
    if (!companyName.trim()) {
      setModalError("Company name is required.");
      return;
    }
    if (!companyDomain.trim()) {
      setModalError("Company domain is required.");
      return;
    }

    setModalBusy(true);
    setModalError("");

    try {
      if (editingCompany) {
        await api("put", `/companies/${editingCompany.company_id}`, {
          name: companyName.trim(),
          domain: companyDomain.trim().toLowerCase(),
        });
        toast.success(`Company '${companyName.trim()}' updated successfully`);
      } else {
        await api("post", "/companies", {
          name: companyName.trim(),
          domain: companyDomain.trim().toLowerCase(),
        });
        toast.success(`Company '${companyName.trim()}' added successfully`);
      }
      setModalOpen(false);
      loadCompanies();
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to save company";
      setModalError(typeof msg === "string" ? msg : "An unexpected error occurred.");
    } finally {
      setModalBusy(false);
    }
  };

  if (!isSuper) {
    return (
      <div className="content" data-testid="settings-page">
        <div className="panel" style={{ textAlign: "center", padding: "48px 24px", marginTop: 24 }}>
          <Shield size={48} color="#ef4444" style={{ margin: "0 auto 16px auto", display: "block" }} />
          <h2 style={{ fontSize: 20, marginBottom: 8, color: "var(--navy)" }}>Access Restricted</h2>
          <p className="muted">The Settings section is accessible only to Super Administrators.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="content" data-testid="settings-page">
      <div className="page-heading">
        <div>
          <div className="small-label">WORKSPACE</div>
          <h1>Settings</h1>
          <p className="muted">
            Signed in as <b>{user.name}</b> · {user.role}
          </p>
        </div>
      </div>

      {/* Workspace Information */}
      <section className="panel" style={{ marginBottom: 24 }} data-testid="workspace-settings-panel">
        <div className="panel-title">
          <div>
            <h3>Workspace</h3>
            <p className="muted">Pinaki Solutions CRM workspace configuration.</p>
          </div>
        </div>
        <div className="facts">
          <div>
            <small>Company</small>
            <b>Pinaki Solutions Pvt. Ltd.</b>
          </div>
          <div>
            <small>Time zone</small>
            <b>Asia/Kolkata (IST)</b>
          </div>
          <div>
            <small>Currency</small>
            <b>INR (₹)</b>
          </div>
          <div>
            <small>Fiscal year</small>
            <b>Apr — Mar</b>
          </div>
          <div>
            <small>Default GST</small>
            <b>18%</b>
          </div>
          <div>
            <small>Default payment terms</small>
            <b>Net 30</b>
          </div>
        </div>
      </section>

      {/* Companies / Organizations Management Section */}
      <section className="panel" data-testid="companies-settings-panel">
        <div
          className="panel-title"
          style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}
        >
          <div>
            <h3 style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Building2 size={18} color="var(--navy)" />
              Companies / Organizations
            </h3>
            <p className="muted" style={{ fontSize: 12.5, marginTop: 2 }}>
              Manage companies and entities available for task assignment and CRM tracking.
            </p>
          </div>

          {isSuper && (
            <button
              className="primary-btn compact"
              onClick={handleOpenAdd}
              data-testid="add-company-btn"
            >
              <Plus size={14} /> Add Company
            </button>
          )}
        </div>

        <div className="table-wrap">
          <table className="panel-table">
            <thead>
              <tr>
                <th style={{ width: "35%" }}>Company Name</th>
                <th style={{ width: "30%" }}>Domain</th>
                <th style={{ width: "15%" }}>Status</th>
                {isSuper && <th style={{ width: "20%", textAlign: "right" }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {loadingCompanies && (
                <tr>
                  <td colSpan={isSuper ? 4 : 3} style={{ textAlign: "center", padding: 24, color: "#64748b" }}>
                    Loading companies...
                  </td>
                </tr>
              )}
              {!loadingCompanies && companies.length === 0 && (
                <tr>
                  <td colSpan={isSuper ? 4 : 3} style={{ textAlign: "center", padding: 32, color: "#64748b" }}>
                    No companies configured. Click "+ Add Company" to create one.
                  </td>
                </tr>
              )}
              {!loadingCompanies &&
                companies.map((comp) => (
                  <tr key={comp.company_id || comp.name} data-testid={`company-row-${comp.company_id}`}>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <Building2 size={15} color="#64748b" />
                        <div>
                          <b style={{ color: "var(--navy)", fontSize: 13.5 }}>{comp.name}</b>
                          {comp.company_id && (
                            <small className="mono" style={{ color: "#94a3b8", display: "block" }}>
                              {comp.company_id}
                            </small>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        <Globe size={13} color="#94a3b8" />
                        <span style={{ fontSize: 13, color: "#334155" }}>{comp.domain}</span>
                      </div>
                    </td>
                    <td>
                      {comp.is_active !== false ? (
                        <span className="badge green" style={{ fontSize: 11, padding: "2px 8px" }}>
                          Active
                        </span>
                      ) : (
                        <span className="badge slate" style={{ fontSize: 11, padding: "2px 8px" }}>
                          Deactivated
                        </span>
                      )}
                    </td>
                    {isSuper && (
                      <td style={{ textAlign: "right" }}>
                        <div style={{ display: "inline-flex", gap: 6 }}>
                          <button
                            className="outline-btn compact"
                            onClick={() => handleOpenEdit(comp)}
                            title="Edit company"
                            data-testid={`edit-company-btn-${comp.company_id}`}
                          >
                            <Edit2 size={12} /> Edit
                          </button>
                          <button
                            className={`outline-btn compact ${comp.is_active !== false ? "danger" : ""}`}
                            onClick={() => handleToggleStatus(comp)}
                            title={comp.is_active !== false ? "Deactivate company" : "Activate company"}
                            data-testid={`toggle-company-status-btn-${comp.company_id}`}
                          >
                            {comp.is_active !== false ? "Deactivate" : "Activate"}
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Add / Edit Company Modal */}
      {modalOpen && (
        <div className="modal-backdrop" data-testid="company-modal-backdrop">
          <form
            className="modal"
            style={{ maxWidth: 480, width: "100%" }}
            onSubmit={handleSaveCompany}
            data-testid="company-form"
          >
            <div className="modal-head" style={{ marginBottom: 16 }}>
              <div>
                <div className="small-label" style={{ letterSpacing: "1.2px", color: "#64748b" }}>
                  {editingCompany ? "EDIT COMPANY" : "NEW COMPANY"}
                </div>
                <h2 style={{ fontSize: 18, marginTop: 4 }}>
                  {editingCompany ? "Edit Company" : "Add Company"}
                </h2>
                <p className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                  Configure organization details for task assignments.
                </p>
              </div>
              <button
                type="button"
                className="icon-btn"
                onClick={() => setModalOpen(false)}
                data-testid="company-modal-close-btn"
              >
                <X size={18} />
              </button>
            </div>

            {modalError && (
              <div className="error" style={{ marginBottom: 16 }} data-testid="company-modal-error">
                <AlertCircle size={14} style={{ marginRight: 6, verticalAlign: "middle" }} />
                {modalError}
              </div>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <label className="dispatch-label">
                  Company Name <span style={{ color: "var(--red)" }}>*</span>
                </label>
                <input
                  type="text"
                  className="dispatch-control"
                  placeholder="e.g. Pinaki Solutions"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  required
                  autoFocus
                  data-testid="company-name-input"
                />
              </div>

              <div>
                <label className="dispatch-label">
                  Company Domain <span style={{ color: "var(--red)" }}>*</span>
                </label>
                <input
                  type="text"
                  className="dispatch-control"
                  placeholder="e.g. pinakisolutions.com"
                  value={companyDomain}
                  onChange={(e) => setCompanyDomain(e.target.value)}
                  required
                  data-testid="company-domain-input"
                />
              </div>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 10,
                marginTop: 22,
                paddingTop: 14,
                borderTop: "1px solid var(--border)",
              }}
            >
              <button
                type="button"
                className="outline-btn"
                onClick={() => setModalOpen(false)}
                disabled={modalBusy}
                data-testid="company-modal-cancel-btn"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="primary-btn"
                disabled={modalBusy}
                data-testid="company-modal-submit-btn"
              >
                {modalBusy
                  ? "Saving..."
                  : editingCompany
                  ? "Update Company"
                  : "Add Company"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
